import React from "react";
import { motion } from "framer-motion";
import {
  DollarSign,
  TrendingUp,
  Brain,
  Zap,
} from "lucide-react";

const roiPoints = [
  {
    metric: "$8K–$20K+/mo",
    title: "Through increased bandwidth, AI grows your production",
    description:
      "Avg $8k/month but we've had practices achieve $20k/mo+ by capturing more patient calls, reactivating more patients, and filling empty schedule gaps with unlimited bandwidth.",
    icon: Zap,
    accentColor: "text-blue-600",
    accentBg: "bg-blue-50",
    accentBorder: "border-blue-100",
    dot: "bg-blue-500",
  },
  {
    metric: "98% NCR",
    title: "Through managing revenue cycle more intelligently",
    description:
      "Cross-reference thousands of payer datapoints that humans could never manually check — AI catches details and patterns across your entire revenue cycle. Target: 98% net collection rate.",
    icon: Brain,
    accentColor: "text-emerald-600",
    accentBg: "bg-emerald-50",
    accentBorder: "border-emerald-100",
    dot: "bg-emerald-500",
  },
  {
    metric: "<30 days",
    title: "Through unlimited bandwidth improving cash flow",
    description:
      "Instant reconciliation, faster appeals, reduced days in AR — AI never sleeps and processes everything the moment it arrives. Target: Under 30 days in AR.",
    icon: TrendingUp,
    accentColor: "text-purple-600",
    accentBg: "bg-purple-50",
    accentBorder: "border-purple-100",
    dot: "bg-purple-500",
  },
  {
    metric: "$5K+/mo",
    title: "Through doing work humans do, not just being software",
    description:
      "No more hold music, manual data entry, or mindless busywork — AI agents actually do the work instead of giving you another tool to operate. Worth $5,000+ in saved time monthly.",
    icon: DollarSign,
    accentColor: "text-amber-600",
    accentBg: "bg-amber-50",
    accentBorder: "border-amber-100",
    dot: "bg-amber-500",
  },
];

const AIROISlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col items-center justify-center p-6 xl:p-10 relative overflow-hidden font-sans">
      {/* Background blurs - matching design system */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/40 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2" />

      {/* Dot Pattern Overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-60" />

      <div className="max-w-[1300px] w-full flex flex-col h-full relative z-10">
        {/* Header */}
        <div className="text-center mb-6 flex-shrink-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-3 border border-blue-100">
              <Brain className="w-4 h-4" />
              <span>The AI Advantage</span>
            </div>

            <h2 className="text-3xl lg:text-5xl font-bold text-slate-900 mb-2 tracking-tight leading-tight">
              Asha's AI OS drives{" "}
              <span className="text-blue-600">ROI for your practice</span> in 4
              ways
            </h2>
            <p className="text-sm text-slate-500 max-w-2xl mx-auto">
              These are real numbers from actual practices using Asha — not
              theoretical projections
            </p>
          </motion.div>
        </div>

        {/* ROI Grid */}
        <div className="flex-1 min-h-0 grid grid-cols-2 gap-4">
          {roiPoints.map((point, i) => (
            <motion.div
              key={point.metric}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.1, duration: 0.4 }}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300 p-5 flex flex-col"
            >
              {/* Metric + Icon */}
              <div className="flex items-center gap-3 mb-3">
                <div
                  className={`w-10 h-10 rounded-xl ${point.accentBg} ${point.accentBorder} border flex items-center justify-center flex-shrink-0`}
                >
                  <point.icon className={`w-5 h-5 ${point.accentColor}`} />
                </div>
                <div
                  className={`text-2xl font-black ${point.accentColor} tracking-tight`}
                >
                  {point.metric}
                </div>
              </div>

              {/* Title */}
              <h3 className="text-base font-bold text-slate-900 mb-2 leading-snug">
                {point.title}
              </h3>

              {/* Description */}
              <p className="text-sm text-slate-500 leading-relaxed flex-1">
                {point.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AIROISlide;
